import type { ActionComputedDelta } from '../compile/action-computed-delta';
import { readItemField } from '../compile/item-field-reader';
import { fromBaseRecord } from '../compile/from-base-context';
import { chargeRowIdentity } from './row-identity';
import type { SpawnRowPlan } from '../spawn/row-plan';
import contract from '../../../catalogs/janus/row-contract.json';
import { statusRows } from '../statuses/expiry';
import { buildAppliedFrame, type AppliedFrameIdentity, type AppliedNewEntity, type AppliedEntityList, type AppliedRow,
    type AppliedRowIdentity, type AppliedTouchedRow, type AppliedFrameResult } from './applied-frame';

/** Base operations carry saved rows; computed status projections belong to the delta. */
export function memberAppliedFrame(identity: AppliedFrameIdentity, members: readonly AppliedRow[],
    newEntities: readonly AppliedNewEntity[] = [], deletes: readonly AppliedRowIdentity[] = [],
    extraRows: readonly AppliedTouchedRow[] = [], delta?: ActionComputedDelta, maxBytes = Infinity): AppliedFrameResult {
    // Retain every applied result for local install; socket parts bound oversized wires.
    const rows = new Map<string, AppliedTouchedRow>(), computed: Record<string, AppliedRow> = {};
    const add = (table: string, row: AppliedRow, key: string, position?: AppliedRow | null): void => {
        const id = String(readItemField(row, key) ?? '').toUpperCase();
        if (table === 'statuses' && deletes.some(removed => removed.table === table && removed.row_id.toUpperCase() === id)) return;
        if (id) rows.set(table + ':' + id, { table, row_id: id, row, ...(position !== undefined ? { position } : {}) });
    };
    const seen = new Set<AppliedRow>();
    const visit = (member: AppliedRow): void => {
        if (seen.has(member)) return;
        seen.add(member);
        for (const [table, key] of Object.entries({ encounter_creatures: 'encounter_creature_id',
            encounters: 'encounter_id', player_characters: 'item_id', inventory: 'inventory_id' }))
            if (fromBaseRecord(member[table])) {
                const raw = member[table];
                add(table, raw, key, ['encounter_creatures', 'inventory'].includes(table)
                    ? fromBaseRecord(member.position) ? member.position : null : undefined);
            }
        const id = String(readItemField(fromBaseRecord(member.encounter_creatures) ? member.encounter_creatures : member, 'encounter_creature_id')
            ?? readItemField(member, 'entity_id') ?? '').toUpperCase();
        if (id && fromBaseRecord(member.computed)) computed[id] = member.computed;
        const base = fromBaseRecord(member._entity_base) && fromBaseRecord(member._entity_base.base) ? member._entity_base.base : null;
        for (const mode of ['target', 'caster'] as const)
            for (const row of statusRows(base, mode)) add('statuses', row, 'status_id');
        for (const key of ['inventory', 'inventory_in_container']) if (Array.isArray(member[key]))
            for (const row of member[key]) if (fromBaseRecord(row)) visit(row);
        if (Array.isArray(member.charges)) for (const row of member.charges)
            if (fromBaseRecord(row)) add('charges', row, 'charge_id');
    };
    members.forEach(visit);
    for (const row of extraRows) {
        const key = row.table + ':' + row.row_id, prior = rows.get(key);
        rows.set(key, prior ? { ...row, row: { ...prior.row, ...row.row } } : row);
    }
    // Every lane uses the same saved creature tombstone, including concentration
    // dismissal and expiry. Retire the member before installing its death counters.
    let departures = deletes;
    for (const row of rows.values()) if (row.table === 'encounter_creatures'
        && Number(readItemField(row.row, 'encounter_creature_deleted') ?? 0) === 1
        && !departures.some(removed => removed.table === row.table && removed.row_id.toUpperCase() === row.row_id.toUpperCase())) {
        if (departures === deletes) departures = [...deletes];
        (departures as AppliedRowIdentity[]).push({ table: row.table, row_id: row.row_id });
    }
    const current = (globalThis as typeof globalThis & { ftActionComputed?: { operationId: string; delta: ActionComputedDelta } }).ftActionComputed;
    const emitted = delta ?? (current?.operationId === identity.operation_id ? current.delta : undefined);
    return buildAppliedFrame({ ...identity, rows: [...rows.values()], computed: emitted ? {} : computed,
        ...(emitted ? { computed_delta: emitted } : {}),
        ...((newEntities as AppliedEntityList).new_inventory?.length ? { new_inventory: (newEntities as AppliedEntityList).new_inventory } : {}), new_entities: newEntities, deletes: departures }, maxBytes);
}

/** Retire only held rows belonging to the browser-decided pools; an absent pool has no row to remove. */
export function retireMemberChargePools(members: readonly AppliedRow[], removals: readonly { holder: string; item_id: string }[]): AppliedRowIdentity[] {
    const deletes = new Map<string, AppliedRowIdentity>();
    for (const member of members) {
        const identity = fromBaseRecord(member.encounter_creatures) ? member.encounter_creatures
            : fromBaseRecord(member.inventory) ? member.inventory : member;
        const id = String(readItemField(identity, 'encounter_creature_id') ?? readItemField(identity, 'inventory_id') ?? '').toUpperCase();
        const pools = removals.filter(change => change.holder.toUpperCase() === id);
        if (!pools.length) continue;
        const matches = (row: AppliedRow): boolean => pools.some(change => change.item_id.toUpperCase() === String(readItemField(row, 'item_id') ?? '').toUpperCase());
        const base = fromBaseRecord(member._entity_base) && fromBaseRecord(member._entity_base.base) ? member._entity_base.base : null;
        for (const row of [...Array.isArray(base?.charges) ? base.charges : [], ...Array.isArray(member.charges) ? member.charges : []])
            if (fromBaseRecord(row) && matches(row)) {
                const chargeId = String(readItemField(row, 'charge_id') ?? '').toUpperCase();
                if (chargeId) deletes.set(chargeId, { table: 'charges', row_id: chargeId });
            }
        if (Array.isArray(member.charges)) member.charges = member.charges.filter(row => !fromBaseRecord(row) || !matches(row));
    }
    return [...deletes.values()];
}

/** Pool changes already decided by the lane, retaining an existing saved identity. */
export function appliedChargeRows(changes: readonly { holder: string; item_id: string; charges: number }[], members: readonly AppliedRow[],
    removals: readonly { holder: string; item_id: string }[] = []): AppliedTouchedRow[] {
    return changes.filter(change => !removals.some(removed => removed.holder.toUpperCase() === change.holder.toUpperCase()
        && removed.item_id.toUpperCase() === change.item_id.toUpperCase())).map(change => {
        const member = members.find(row => fromBaseRecord(row.encounter_creatures)
            && String(readItemField(row.encounter_creatures, 'encounter_creature_id')).toUpperCase() === change.holder.toUpperCase());
        const base = fromBaseRecord(member?._entity_base) && fromBaseRecord(member._entity_base.base) ? member._entity_base.base : member;
        const saved = Array.isArray(base?.charges) ? base.charges.find(row => fromBaseRecord(row)
            && Number(readItemField(row, 'charge_deleted') ?? 0) === 0
            && String(readItemField(row, 'item_id')).toUpperCase() === change.item_id.toUpperCase()) as AppliedRow | undefined : undefined;
        const id = String(readItemField(saved ?? {}, 'charge_id') ?? chargeRowIdentity(change.holder, change.item_id)).toUpperCase();
        return { table: 'charges', row_id: id, row: { ...saved, charge_id: id, charge_holder: change.holder,
            item_id: change.item_id, charges: change.charges, charge_deleted: 0 } };
    });
}

/** Row-plan births already carry browser identities, including floor inventory and summon loadouts. */
export function appliedSpawnRows(plan: SpawnRowPlan | undefined): AppliedTouchedRow[] {
    return (plan?.writes ?? []).map(write => {
        const spec = contract[write.table];
        const row = write.table === 'encounter_creature_swarm_states' ? { ...write.before, ...write.row } : write.row;
        return { table: 'table' in spec ? spec.table : write.table, row_id: String(readItemField(write.row, spec.key) ?? '').toUpperCase(), row, ...(write.op === 'update' ? { partial: true as const } : {}) };
    });
}
